import {Badge} from "../../components/Badge";
import {
    Link,
    LoaderFunctionArgs,
    useLoaderData,
    useNavigate,
    useRevalidator,
} from "react-router-dom";
import {createApolloClient} from "../../providers/ApolloClientFactory";
import {organizationStore} from "../../store/organization";
import {UPDATE_EVENT} from "./mutation";
import {GET_LIST_EVENTS} from "./queries";
import {Event} from "../../types/event";
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import moment from "moment/moment";
import {usePermissions} from "../auth/api";
import {eventStore} from "../../store/event";
import {
    ColDef,
    GridApi,
    ICellEditorParams,
    SizeColumnsToContentStrategy,
    SuppressKeyboardEventParams,
} from "ag-grid-community";
import {Input} from "../../components/inputs/Input";
import {PrimaryButton, SecondaryButton} from "../../components/Button";
import {AgGridReact} from "ag-grid-react";
import {userStore} from "../../store/user";
import Switch from "react-switch";
import {useGridEdit, whereByIdNum} from "../helpers/GridEditHelper";
import TextSuggestEditor from "../helpers/TextSuggestEditorHelper";
import GridEditActions from "../helpers/GridEditActions";
import {GET_LIST_PAYMENT_PROCESSORS_BY_ORGANIZATION} from "../../queries";
import {useGridSave} from "../helpers/UseGridSave";

const client = createApolloClient();
const {getState} = organizationStore;
const {getState: getEventStore} = eventStore;
const {getState: getUserStore} = userStore;

const buildSearch = (search: string) => [
    {
        name: {
            _ilike: `%${search}%`,
        },
    },
];

export const eventsListLoader = async ({request}: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const search = url.searchParams.get("search");
    const rawOrganizationId = getState().organizationId;
    const organizationId = parseInt(
        rawOrganizationId !== null ? rawOrganizationId.toString() : "",
        10
    );
    const user = getUserStore().user;

    let where: any = {_or: search ? buildSearch(search) : undefined};
    if (organizationId && organizationId !== 0) {
        where = {...where, organization_id: {_eq: organizationId}};
    }
    if ((user?.events?.length || 0) > 0) {
        where = {...where, id: {_in: user?.events}};
    }

    const [{data: eventsData}, {data: ppData}] = await Promise.all([
        client.query({query: GET_LIST_EVENTS, variables: {where}}),
        client.query({
            query: GET_LIST_PAYMENT_PROCESSORS_BY_ORGANIZATION,
            variables: organizationId
                ? {where: {organization_id: {_eq: organizationId}}}
                : {where: {}},
            fetchPolicy: "network-only",
        }),
    ]);

    return {
        events: eventsData.events as Event[],
        paymentProcessors: ppData.payment_processor_config as Array<{
            id: number;
            payment_processor: string;
        }>,
    };
};

const DateCellEditor = forwardRef(function DateCellEditor(
    props: ICellEditorParams,
    ref
) {
    const inputRef = useRef<HTMLInputElement>(null);
    const initial =
        props.value && String(props.value).length > 0
            ? moment(props.value).format("YYYY-MM-DD")
            : "";

    const [val, setVal] = useState<string>(initial);

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 0);
    }, []);

    useImperativeHandle(ref, () => ({
        getValue() {
            return val;
        },
    }));

    return (
        <input
            ref={inputRef}
            type="date"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            style={{
                width: "100%",
                height: "100%",
                boxSizing: "border-box",
                border: "none",
                outline: "none",
                background: "transparent",
            }}
        />
    );
});

export const EventsList = () => {
    const {editCreatePermission} = usePermissions("events");
    const {editCreatePermission: paymentProcessorEditPermission} =
        usePermissions("payment_processors");
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const gridRef = useRef<GridApi>();
    const originalActiveRef = useRef<Map<string, boolean>>(new Map());

    const {events, paymentProcessors} = useLoaderData() as {
        events: Event[];
        paymentProcessors: { id: number; payment_processor: string }[];
    };

    const getPatchedValue = (p: any, field?: string) => {
        if (!field) return p.value;
        const id = String(p.data?.id ?? "");
        const patch = p.context?.pending?.[id];
        if (patch && Object.prototype.hasOwnProperty.call(patch, field)) {
            return (patch as any)[field];
        }
        return p.data?.[field];
    };

    const [rowData, setRowData] = useState<Event[]>([]);
    const [isActiveToggle, setIsActiveToggle] = useState(true);
    const {pending, pendingCount, resetBaseline, makeEditable, setPending, getBaselineRows} =
        useGridEdit<Event>();
    const getRowId = useCallback((p: { data: Event }) => String(p.data.id), []);

    const onActiveOnlyToggle = (next: boolean) => {
        setIsActiveToggle(next);
        gridRef.current?.onFilterChanged();
    };

    const gridContext = useMemo(() => ({pending}), [pending]);

    const gridOptions = useMemo(() => ({
        enableCellTextSelection: true,
        context: gridContext,
    }), [gridContext]);

    const fmtDate = (v: any) => (v ? moment(v).format("MM/DD/YYYY") : "");
    const parseDate = (nv: any, ov: any) => {
        const s = String(nv ?? "").trim();
        if (!s) return ov;
        const m = moment(s, ["MM/DD/YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
        return m.isValid() ? m.toISOString() : ov;
    };

    const paymentProcessorList = useMemo(
        () =>
            (paymentProcessors ?? [])
                .map((p) => ({id: Number(p.id), name: String(p.payment_processor).trim()}))
                .filter((p) => Number.isFinite(p.id) && p.name.length > 0)
                .sort((a, b) => a.name.localeCompare(b.name)),
        [paymentProcessors]
    );

    const ppIdToName = useMemo(
        () => new Map(paymentProcessorList.map((p) => [p.id, p.name] as const)),
        [paymentProcessorList]
    );
    const ppNameToId = useMemo(
        () => new Map(paymentProcessorList.map((p) => [p.name, p.id] as const)),
        [paymentProcessorList]
    );

    const TZ_CODE_TO_LABEL: Record<string, string> = useMemo(
        () => ({
            ADT: "Atlantic Daylight Time",
            AST: "Atlantic Standard Time",
            EDT: "Eastern Daylight Time",
            EST: "Eastern Standard Time",
            CDT: "Central Daylight Time",
            CST: "Central Standard Time",
            MDT: "Mountain Daylight Time",
            MST: "Mountain Standard Time",
            PDT: "Pacific Daylight Time",
            PST: "Pacific Standard Time",
        }),
        []
    );
    const TZ_CODES = useMemo(
        () => ["ADT", "AST", "EDT", "EST", "CDT", "CST", "MDT", "MST", "PDT", "PST"] as const,
        []
    );
    const TZ_LABEL_TO_CODE = useMemo(() => {
        const m = new Map<string, string>();
        Object.entries(TZ_CODE_TO_LABEL).forEach(([code, label]) => {
            m.set(label, code);
        });
        return m;
    }, [TZ_CODE_TO_LABEL]);

    useEffect(() => {
        resetBaseline(events);
        setRowData(events);
    }, [events, resetBaseline]);

    useEffect(() => {
        gridRef.current?.refreshCells({ force: true });
        gridRef.current?.refreshClientSideRowModel("filter");
    }, [pending]);

    useEffect(() => {
        originalActiveRef.current = new Map(
            (events ?? []).map(e => [String(e.id), !!e.is_active])
        );
    }, [events]);

    useEffect(() => {
        const api = gridRef.current;
        if (!api) return;
        const model = isActiveToggle
            ? { is_active: { filterType: 'text', type: 'equals', filter: 'Yes' } }
            : null;
        api.setFilterModel(model);
        api.onFilterChanged();
    }, [isActiveToggle, pendingCount]);

    const linkRenderer = useCallback(
        (permission: boolean, linkFunc: (p: { data: Event }) => string) =>
            (params: any) => {
                if (params.value === undefined) return null;
                return permission ? (
                    <Link
                        to={linkFunc(params)}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <Badge>{params.value}</Badge>
                    </Link>
                ) : (
                    <Badge>{params.value}</Badge>
                );
            },
        []
    );

    const colDefs: ColDef<Event>[] = useMemo(
        () => [
            {
                headerName: "Id",
                valueGetter: (p) => p.data?.id,
                cellRenderer: linkRenderer(
                    !!editCreatePermission,
                    (p) => `/events/${p.data.id}/edit`
                ),
                filter: "agTextColumnFilter",
                pinned: "left",
            },
            {
                headerName: "Event Name",
                field: "name",
                ...makeEditable(
                    "name",
                    {
                        type: "text",
                        canEdit: !!editCreatePermission
                    }, setRowData),
                filter: "agTextColumnFilter",
                pinned: "left",
            },
            {
                headerName: "Is Active?",
                field: "is_active",
                cellDataType: "text",
                sortable: true,
                filter: "agTextColumnFilter",
                ...makeEditable(
                    "is_active",
                    {
                        type: "boolean",
                        canEdit: !!editCreatePermission,
                        toggleOnClick: true
                    }, setRowData),

                valueFormatter: (p) => (getPatchedValue(p, "is_active") ? "Yes" : "No"),

                filterValueGetter: (p: any) => {
                    const id = String(p.data?.id ?? "");
                    const patch = p.context?.pending?.[id] as { is_active?: boolean } | undefined;

                    if (patch && Object.prototype.hasOwnProperty.call(patch, "is_active")) {
                        return patch.is_active ? "Yes" : "No";
                    }

                    const baseline = originalActiveRef.current.get(id);
                    return baseline ? "Yes" : "No";
                },
            },
            {
                headerName: "Payment Processor",
                field: "payment_processor_id",
                ...makeEditable(
                    "payment_processor_id",
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        parser: (nv: any, ov: any) => {
                            const s = String(nv ?? "").trim();
                            if (!s) return ov;
                            const byName = ppNameToId.get(s);
                            if (byName != null) return byName;
                            const n = Number(s);
                            return !Number.isNaN(n) && ppIdToName.has(n) ? n : ov;
                        },
                        formatter: (v: any) => ppIdToName.get(Number(v)) ?? "",
                        equals: (a: any, b: any) => Number(a ?? 0) === Number(b ?? 0),
                    },
                    setRowData),

                cellEditor: TextSuggestEditor as any,
                cellEditorParams: (p: { value: any; data: Event }) => ({
                    values: paymentProcessorList.map((pp) => pp.name),
                    showAllOnEdit: true,
                    initialValue:
                        ppIdToName.get(Number(p.value ?? p.data?.payment_processor_id)) ?? "",
                }),

                cellEditorPopup: true,
                suppressKeyboardEvent: (params) => params.editing === true,

                cellRenderer: (p: { value: any; data: Event }) => {
                    const id = Number(p.value ?? p.data?.payment_processor_id);
                    const name = ppIdToName.get(id) ?? "";
                    if (!name) return null;
                    return editCreatePermission ? (
                        <Badge>{name}</Badge>
                    ) : paymentProcessorEditPermission ? (
                        <Link
                            to={`/payment-processors/${id}/edit`}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <Badge>{name}</Badge>
                        </Link>
                    ) : (
                        <Badge>{name}</Badge>
                    );
                },

                filter: "agTextColumnFilter",
                filterValueGetter: (p) => {
                    const id = Number((p.data as Event).payment_processor_id);
                    return ppIdToName.get(id) ?? "";
                },
            },
            {
                headerName: "Dynamic Descriptor",
                field: "dynamic_descriptor",
                ...makeEditable(
                    "dynamic_descriptor",
                    {type: "text", canEdit: !!editCreatePermission},
                    setRowData
                ),
                filter: "agTextColumnFilter",
            },
            {
                headerName: "Timezone",
                field: "timezone",
                ...makeEditable(
                    "timezone",
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        parser: (nv: any, ov: any) => {
                            const raw = String(nv ?? "").trim();
                            if (!raw) return ov;
                            if (TZ_CODE_TO_LABEL[raw]) return raw; // user typed a code
                            const byLabel = TZ_LABEL_TO_CODE.get(raw);
                            return byLabel ?? ov; // user picked/typed a label
                        },
                        formatter: (v: any) => TZ_CODE_TO_LABEL[String(v ?? "").trim()] ?? "",
                        equals: (a: any, b: any) =>
                            String(a ?? "").trim() === String(b ?? "").trim(),
                    },
                    setRowData
                ),
                cellEditor: TextSuggestEditor as any,
                cellEditorParams: (p: { value: any; data: Event }) => ({
                    values: TZ_CODES.map((c) => TZ_CODE_TO_LABEL[c]),
                    showAllOnEdit: true,
                    initialValue:
                        TZ_CODE_TO_LABEL[
                            String(p.value ?? p.data?.timezone ?? "").trim()
                            ] ?? "",
                }),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    TZ_CODE_TO_LABEL[String(getPatchedValue(p, "timezone") ?? "").trim()] ?? "",
            },

            {
                headerName: "Start Date",
                field: "start_date",
                ...makeEditable(
                    "start_date",
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        parser: parseDate,
                        formatter: fmtDate,
                    },
                    setRowData
                ),
                valueFormatter: (p) => fmtDate(p.value),
                cellEditor: DateCellEditor as any,
                filter: "agDateColumnFilter",
            },
            {
                headerName: "End Date",
                field: "end_date",
                ...makeEditable(
                    "end_date",
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        parser: parseDate,
                        formatter: fmtDate,
                    },
                    setRowData
                ),
                valueFormatter: (p) => fmtDate(p.value),
                cellEditor: DateCellEditor as any,
                filter: "agDateColumnFilter",
            },

            {
                headerName: "Updated By",
                valueGetter: (p) => p.data?.user?.username,
                filter: "agTextColumnFilter",
            },
            {
                headerName: "Created Date",
                valueGetter: (p) => p.data?.created_at,
                valueFormatter: (p) =>
                    p.value ? moment(p.value).format("MM/DD/YYYY h:mm:ss a") : "",
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    p.data?.created_at ? moment(p.data.created_at).format("MM/DD/YYYY h:mm:ss a") : "",
            },
            {
                headerName: "Updated Date",
                valueGetter: (p) => p.data?.updated_at,
                valueFormatter: (p) =>
                    p.value ? moment(p.value).format("MM/DD/YYYY h:mm:ss a") : "",
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    p.data?.updated_at ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm:ss a") : "",
            },
        ],
        [
            editCreatePermission,
            paymentProcessorEditPermission,
            makeEditable,
            setRowData,
            linkRenderer,
            paymentProcessorList,
            ppIdToName,
            ppNameToId,
            TZ_CODE_TO_LABEL,
            TZ_LABEL_TO_CODE,
            TZ_CODES,
        ]
    );

    const defaultColDef = useMemo(() => ({
        valueGetter: (p: any) => p.colDef.field ? getPatchedValue(p, p.colDef.field) : p.value,
    }), []);

    const rowHeight = 50;
    const autoSizeStrategy: SizeColumnsToContentStrategy = {
        type: "fitCellContents",
    };
    const handleExportCSV = () => {
        if (gridRef.current) {
            const timestamp = new Date().toISOString().replace(/[-:]/g, "");
            const params = {
                fileName: `Events_${timestamp}.csv`,
                columnSeparator: ",",
            };
            gridRef.current.exportDataAsCsv(params);
        }
    };

    const mutateRow = async (id: number, patch: Partial<Event>) => {
        const user = getUserStore().user;
        const input = {
            ...patch,
            last_updated_by: user?.id,
            updated_at: new Date().toISOString()
        };
        const res = await client.mutate({
            mutation: UPDATE_EVENT,
            variables: {id, input},
            errorPolicy: "all",
        });
        if (res.errors?.length)
            throw new Error(res.errors.map((e) => e.message).join("; "));
        if (!res.data?.update_events_by_pk?.id)
            throw new Error(`No id returned for ${id}`);
    };

    const { handleSave } = useGridSave<Event>({
        rowData,
        setRowData,
        pending,
        setPending,
        resetBaseline,
        gridApiRef: gridRef as any,
        revalidate: () => revalidator.revalidate(),
        mutateRow,
        optimistic: true,
        baselines: [
            { ref: originalActiveRef, value: (r) => !!r.is_active },
        ],
    });

    return (
        <div className="w-full flex flex-col overflow-hidden" style={{ height: "calc(100vh - 128px)" }}>
            <div className="md:hidden grid grid-cols-6 min-w-0 px-0 py-0">
                <div className="col-span-6 flex items-center justify-between min-h-[48px] mb-1">
                    <div className="flex items-center gap-2 pl-2 pt-2">
                        <GridEditActions
                            pendingCount={pendingCount}
                            pending={pending}
                            gridRef={gridRef}
                            resetBaseline={resetBaseline}
                            baselineData={events}
                            getBaselineRows={getBaselineRows}
                            isActiveToggle={isActiveToggle}
                            setRowData={setRowData}
                            setPending={setPending}
                            onSave={handleSave}
                        />
                    </div>

                    <div className="ml-2 flex flex-col items-center">
                        <span className="block text-xs font-medium text-gray-500 whitespace-nowrap">Active Only</span>
                        <Switch
                            checked={isActiveToggle}
                            onChange={onActiveOnlyToggle}
                            onColor="#EE2E30"
                            onHandleColor="#FFFFFF"
                            handleDiameter={18}
                            uncheckedIcon={false}
                            checkedIcon={false}
                            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                            height={15}
                            width={48}
                        />
                    </div>
                </div>

                <div className="col-span-6 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                        <div className="w-full px-3 py-2">
                            <Input
                                name="search"
                                placeholder="Search Events"
                                onChange={(s) => {
                                    navigate(s && typeof s === "string" && s.length > 0 ? `/events/list?search=${s}` : `/events/list`);
                                    revalidator.revalidate();
                                }}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                        <SecondaryButton onClick={handleExportCSV}>Export to CSV</SecondaryButton>
                        {editCreatePermission && (
                            <PrimaryButton onClick={() => navigate(`/events/create`)}>Create</PrimaryButton>
                        )}
                    </div>
                </div>
            </div>

            <div className="hidden md:grid grid-cols-12 items-center gap-3 min-w-0 py-2">
                <div className="col-span-5 min-w-0">
                    <div className="w-full">
                        <Input
                            name="search"
                            placeholder="Search Events"
                            onChange={(s) => {
                                navigate(s && typeof s === "string" && s.length > 0 ? `/events/list?search=${s}` : `/events/list`);
                                revalidator.revalidate();
                            }}
                        />
                    </div>
                </div>

                <div className="col-span-3 col-start-6 justify-self-start flex items-center gap-1">
                    <GridEditActions
                        pendingCount={pendingCount}
                        pending={pending}
                        gridRef={gridRef}
                        resetBaseline={resetBaseline}
                        baselineData={events}
                        getBaselineRows={getBaselineRows}
                        isActiveToggle={isActiveToggle}
                        setRowData={setRowData}
                        setPending={setPending}
                        onSave={handleSave}
                    />
                </div>

                <div className="col-span-1 flex flex-col items-center">
                    <span className="block text-sm font-medium text-gray-500 whitespace-nowrap">Active Only</span>
                    <Switch
                        checked={isActiveToggle}
                        onChange={onActiveOnlyToggle}
                        onColor="#EE2E30"
                        onHandleColor="#FFFFFF"
                        handleDiameter={18}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                        height={15}
                        width={48}
                    />
                </div>

                <div className="col-span-3 flex justify-end items-center gap-1 whitespace-nowrap">
                    <SecondaryButton onClick={handleExportCSV}>
                        <span className="whitespace-nowrap">Export&nbsp;to&nbsp;CSV</span>
                    </SecondaryButton>
                    {editCreatePermission && (
                        <PrimaryButton onClick={() => navigate(`/events/create`)}>Create</PrimaryButton>
                    )}
                </div>
            </div>

            <div className="flex-1 min-h-0">
                <div className="ag-theme-quartz w-full h-full min-w-0">
                    <AgGridReact
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        rowHeight={rowHeight}
                        autoSizeStrategy={autoSizeStrategy}
                        pagination
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        getRowId={getRowId}
                        onGridReady={(params) => {
                            gridRef.current = params.api;
                            if (isActiveToggle) {
                                const model = { is_active: { filterType: 'text', type: 'equals', filter: 'Yes' } };
                                params.api.setFilterModel(model);
                                params.api.onFilterChanged();
                            }
                        }}
                        singleClickEdit
                        stopEditingWhenCellsLoseFocus
                    />
                </div>
            </div>
        </div>
    );
};
